import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './number-of-rooms.reducer';

export const NumberOfRooms = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const numberOfRoomsList = useAppSelector(state => state.numberOfRooms.entities);
  const loading = useAppSelector(state => state.numberOfRooms.loading);
  const totalItems = useAppSelector(state => state.numberOfRooms.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="number-of-rooms-heading" data-cy="NumberOfRoomsHeading">
        <Translate contentKey="apiLocationApp.numberOfRooms.home.title">Number Of Rooms</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.numberOfRooms.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/number-of-rooms/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.numberOfRooms.home.createLabel">Create new Number Of Rooms</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {numberOfRoomsList && numberOfRoomsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('oneRoom')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.oneRoom">One Room</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('oneRoom')} />
                </th>
                <th className="hand" onClick={sort('twoRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.twoRooms">Two Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('twoRooms')} />
                </th>
                <th className="hand" onClick={sort('threeRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.threeRooms">Three Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('threeRooms')} />
                </th>
                <th className="hand" onClick={sort('fourRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.fourRooms">Four Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fourRooms')} />
                </th>
                <th className="hand" onClick={sort('fiveRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.fiveRooms">Five Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fiveRooms')} />
                </th>
                <th className="hand" onClick={sort('sixRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.sixRooms">Six Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sixRooms')} />
                </th>
                <th className="hand" onClick={sort('sevenRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.sevenRooms">Seven Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sevenRooms')} />
                </th>
                <th className="hand" onClick={sort('eightRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.eightRooms">Eight Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('eightRooms')} />
                </th>
                <th className="hand" onClick={sort('nineOrMoreRooms')}>
                  <Translate contentKey="apiLocationApp.numberOfRooms.nineOrMoreRooms">Nine Or More Rooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('nineOrMoreRooms')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {numberOfRoomsList.map((numberOfRooms, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/number-of-rooms/${numberOfRooms.id}`} color="link" size="sm">
                      {numberOfRooms.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${numberOfRooms.geographicLevel}`} />
                  </td>
                  <td>{numberOfRooms.date}</td>
                  <td>{numberOfRooms.geography}</td>
                  <td>{numberOfRooms.geographyCode}</td>
                  <td>{numberOfRooms.totalAllHouseholds}</td>
                  <td>{numberOfRooms.oneRoom}</td>
                  <td>{numberOfRooms.twoRooms}</td>
                  <td>{numberOfRooms.threeRooms}</td>
                  <td>{numberOfRooms.fourRooms}</td>
                  <td>{numberOfRooms.fiveRooms}</td>
                  <td>{numberOfRooms.sixRooms}</td>
                  <td>{numberOfRooms.sevenRooms}</td>
                  <td>{numberOfRooms.eightRooms}</td>
                  <td>{numberOfRooms.nineOrMoreRooms}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/number-of-rooms/${numberOfRooms.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/number-of-rooms/${numberOfRooms.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/number-of-rooms/${numberOfRooms.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.numberOfRooms.home.notFound">No Number Of Rooms found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={numberOfRoomsList && numberOfRoomsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default NumberOfRooms;
